import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { DashboardsContext } from "../contexts/DashboardsContext";
import { GroupSettingsContext } from "../contexts/GroupSettingsContext";
import { TableContext } from "../contexts/TableContext";
import { DashboardsTableContext, DashboardsTableFilters } from "../contexts/TablesContext/DashboardsTableProvider";
import { WorkspacesContext } from "../contexts/WorkspacesContext";
import { Dashboard, EmailBody } from "../types/files";
import ArrayUtils from "../utils/ArrayUtils";
import useBackwardsCompatible from "./useBackwardsCompatible";
import useCompaniesTableColumns from "./useCompaniesTableColumns";
import useDashboards from "./useDashboards";

const useDashboardsTable = (workspaceId?: string, search?: string) => {
  const { settingsLoaded, dashboardColumns } = useContext(GroupSettingsContext);
	const { setHeaders, setVisibleHeaders } = useContext(TableContext);
  const { workspaceScores } = useContext(WorkspacesContext);
  const { dashboardsLoaded, dashboards, mappedOverviewQueries } = useContext(DashboardsContext);
  const { loading, sortBy, columns, rows: dashboardArray, filters,
    setLoading, setRows: setDashboardArray, setFilters, setPreFiltered } = useContext(DashboardsTableContext);
  const { defaultColumns } = useCompaniesTableColumns();
  const { filterDashboardList } = useDashboards();
  const { dashboardWorkspaces, dashboardSource, dashboardOwner } = useBackwardsCompatible();

  const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

  const hasDashboards = useMemo(() => !!dashboards?.length, [dashboards]);

  const filteredColumns = useMemo(() => ['Name', ...(columns ?? [])], [columns]);

  const sortedDashboards = useMemo(() => {
    let sorted: Dashboard[] = [...dashboardArray ?? []];

    if (sortBy.includes('Name')) {
      sorted.sort((prev: Dashboard, next: Dashboard) => prev.title?.localeCompare(next.title));
    } else if (sortBy.includes('Recent Activity')) {
      return sorted.sort((dA, dB) => {
        const emailItemsA: EmailBody[]  = ArrayUtils.sortByDescending([...dA?.emailBodies
          ?.filter(emailBody => ['EMAIL', 'FULL_EMAIL', 'CALL'].includes(emailBody?.type || '')) || []], 'createdAt');
        const emailItemsB: EmailBody[]  = ArrayUtils.sortByDescending([...dB?.emailBodies
          ?.filter(emailBody => ['EMAIL', 'FULL_EMAIL', 'CALL'].includes(emailBody?.type || '')) || []], 'createdAt');

        return moment(emailItemsB?.[0]?.createdAt || dB.createdAt).diff(moment(emailItemsA?.[0]?.createdAt || dA.createdAt));
      });
    } else if (sortBy.includes('Fit score')) {
      sorted = Array.from(new Map(
        sorted.filter(dashboard => dashboardWorkspaces(dashboard)?.includes(workspaceId || ''))
          .map(dashboard => [dashboard,
            workspaceScores.find(wsScore => wsScore.workspaceId === workspaceId)?.scores
              ?.find(({dashboardId}) => dashboardId === dashboard.id)?.score || 0
          ]) ?? [])
      ).sort((a, b) => b[1] - a[1]).map(entry => entry[0]);
    } else if (sortBy.includes('Updated')) {
      return sorted.sort((dA, dB) => moment(dB.updatedAt).diff(moment(dA.updatedAt)));
    } else if (sortBy.includes('Added')) {
      return sorted.sort((dA, dB) => moment(dB.createdAt).diff(moment(dA.createdAt)));
    }

    return sorted;
  // eslint-disable-next-line
  }, [dashboardArray, workspaceScores, workspaceId, sortBy]);

  const filteredDashboards = useMemo(() => {
    let filtered: Dashboard[] = sortedDashboards;

    if (!!filters.recent?.length) {
      filtered = filtered?.filter(dashboard => {
        const emailItems: EmailBody[]  =  ArrayUtils.sortByDescending([...dashboard?.emailBodies
            ?.filter(emailBody => ['NEW_DEAL', 'COMPANY_UPDATE', 'NEW_FUND_OPPORTUNITY', 'FUND_PORTFOLIO_UPDATE'].includes(emailBody?.contentClassification || '')) || []], 'createdAt')

        if (emailItems?.[0]?.contentClassification === 'COMPANY_UPDATE')
          return filters.recent?.includes('Company update');
        else if (emailItems?.[0]?.contentClassification === 'NEW_FUND_OPPORTUNITY')
          return filters.recent?.includes('New fund opportunity');
        else if (emailItems?.[0]?.contentClassification === 'FUND_PORTFOLIO_UPDATE')
          return filters.recent?.includes('Fund portfolio update');
        else
          return filters.recent?.includes('New deal');
      });
    }
    if (!!filters.statuses?.length) {
      const statuses = filters.statuses?.map(status => status.toLowerCase());

      filtered = filtered?.filter(({status}) => statuses.includes(status.toLowerCase()));
    }
    if (!!filters.stages?.length) {
      filtered = filtered?.filter(dashboard =>
        filters.stages?.some(key => key === dashboard.investmentStage || ''));
    }
    if (!!filters.tagGroups?.length) {
      filtered = filtered?.filter(dashboard =>
        filters.tagGroups?.every(tagGroup => {
          const lookUpDashboardTagGroup = dashboard.tagGroups?.find(({title}) => title === tagGroup.title);

          if (!!lookUpDashboardTagGroup)
            return tagGroup.tags?.some(tag => lookUpDashboardTagGroup.tags?.includes(tag));

          return false;
        }));
    }
    if (!!filters.keyPeopleTags?.length) {
      filtered = filtered?.filter(dashboard => {
        const keyPeople = mappedOverviewQueries.get(`${dashboard.id}:Key People`);
        const keyPeopleTags = Array.from(new Set(keyPeople?.flatMap((person: any) => person?.tags ?? [])) ?? []) as string[];

        if (!!keyPeopleTags?.length)
          return filters.keyPeopleTags?.some(tag => !!keyPeopleTags.find(keyTag => keyTag === tag));

        return false;
      });
    }
    if (!!filters.locations?.length) {
      filtered = filtered?.filter(dashboard => {
        const location = mappedOverviewQueries.get(`${dashboard.id}:Location`);

        return filters.locations?.some(key => location?.toLowerCase() === key?.toLowerCase());
      });
    }
    if (!!filters.owners?.length) {
      filtered = filtered?.filter(dashboard => filters.owners?.some(src =>
        (src?.name?.toLowerCase() === dashboardOwner(dashboard)?.name?.toLowerCase())
          && (src?.email?.toLowerCase() === dashboardOwner(dashboard)?.email?.toLowerCase())));
    }
    if (!!filters.sources?.length) {
      filtered = filtered?.filter(dashboard => filters.sources?.some(src =>
        (src?.name?.toLowerCase() === dashboardSource(dashboard)?.name?.toLowerCase())
          && (src?.email?.toLowerCase() === dashboardSource(dashboard)?.email?.toLowerCase())));
    }

    return filtered;
  // eslint-disable-next-line
  }, [filters, sortedDashboards]);

  const handleFilterRows = useCallback(({
    recent, statuses, stages, tagGroups, keyPeopleTags, locations, owners, sources, added
  }: DashboardsTableFilters) => {
    setFilters(prev => ({
      recent: recent ?? prev.recent,
      statuses: statuses ?? prev.statuses,
      stages: stages ?? prev.stages,
      tagGroups: tagGroups ?? prev.tagGroups,
      keyPeopleTags: keyPeopleTags ?? prev.keyPeopleTags,
      locations: locations ?? prev.locations,
      owners: owners ?? prev.owners,
      sources: sources ?? prev.sources,
      added: added ?? prev.added,
    }));
  }, [setFilters]);

  const handlePreFilteredRows = useCallback(({ recent }: DashboardsTableFilters) => {
    setFilters(prev => ({...prev, recent: recent ?? prev.recent }));
    setPreFiltered(true);
  }, [setFilters, setPreFiltered]);

  useEffect(() => {
    if (settingsLoaded) {
      const defaultHeaders = defaultColumns.map(col => col.id) as string[];

      if (!!dashboardColumns?.length) {
        const oldHeaders = dashboardColumns.filter(({name}) => !defaultHeaders.some(colId => colId === name)).map(({name}) => name);
        const newHeaders = defaultHeaders.filter(colId => !dashboardColumns.some(({name}) => name === colId));
        let orderedHeaders = [...dashboardColumns];

        if (!!newHeaders.length) {
          if (!!oldHeaders.length)
            orderedHeaders = orderedHeaders.map(header => oldHeaders.includes(header.name) ? ({name: newHeaders[0], isHidden: false}) : header);
          else
            orderedHeaders = [...orderedHeaders, ...newHeaders.map(header => ({name: header, isHidden: false}))];
        } else if (!!oldHeaders.length) {
          orderedHeaders = orderedHeaders.filter(header => !oldHeaders.includes(header.name));
        }
        orderedHeaders = Array.from(new Map(orderedHeaders.map(item => [item.name, item])).values());

        setHeaders(orderedHeaders.map(({name}) => name));
        setVisibleHeaders(orderedHeaders.map(({isHidden}) => !isHidden));
      } else {
        setHeaders(defaultHeaders);
        setVisibleHeaders(defaultHeaders.map(() => true));
      }
    }
    // eslint-disable-next-line
  }, [settingsLoaded]);

  useEffect(() => {
    if (dashboardsLoaded) {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        clearTimeout(timerRef.current);
        setDashboardArray(filterDashboardList(search || '', workspaceId));
        setLoading(false);
      }, 500);
    }

    return () => {
      clearTimeout(timerRef.current);
    }
  // eslint-disable-next-line
  }, [dashboardsLoaded, filterDashboardList, search]);

  return {
    loading,
    hasDashboards,
    columns: filteredColumns,
    rows: filteredDashboards,
    filters,
    handleFilterRows,
    handlePreFilteredRows,
  };
};

export default useDashboardsTable;